import React, {
  ComponentProps,
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
} from "react";
import {twMerge} from "tailwind-merge";
import classNames from "classnames";
import {OutsideClickHandler} from "../OutsideClickHandler";
import {InputFieldProps, InputFieldContainer} from "../InputField";
import {Label} from "../InputField/Label";
import {useSelect} from "./useSelect";

export interface OptionProps extends ComponentProps<"span"> {
  value: string;
  label: string;
  selected?: boolean;
}

export interface SelectFieldProps extends InputFieldProps, FormRequiredProps {
  onValueChange?: (value: string) => void;
  transitionedLabel?: boolean;
}

interface FormRequiredProps {
  invalidationMessage?: string;
}

export const SelectFieldContext = createContext(
  {} as {
    InputRef: React.MutableRefObject<HTMLInputElement | null>;
    setShowOptions: React.Dispatch<React.SetStateAction<boolean>>;
    handleChange: (value: string, label: string) => void;
    showOptions?: boolean;
    layout: "underlined" | "bordered";
    label?: string;
  } & ComponentProps<"input">,
);

const Options = ({
  className,
  value,
  label,
  selected,
  ...restProps
}: OptionProps) => {
  const {handleChange} = useContext(SelectFieldContext);

  useEffect(() => {
    if (selected) {
      setTimeout(() => handleChange(value, label), 0);
    }
  }, [selected]);

  return (
    <span
      onClick={() => handleChange(value, label)}
      className={twMerge(
        "px-2 py-1 hover:bg-primary-50 cursor-pointer text-primaryBlue-700 text-sm block w-full",
        className,
      )}
      {...restProps}
    >
      {label}
    </span>
  );
};

const SelectFieldHelperInput = ({
  className,
  onSelectClick,
  ...restProps
}: ComponentProps<"input"> & {onSelectClick: () => void}) => {
  const {InputRef, layout, label} = useContext(SelectFieldContext);

  return (
    <div
      onClick={onSelectClick}
      className={classNames(
        {bordered: "border-2 rounded", underlined: "border-b-2"}[layout],
        "peer relative flex py-1.5 w-full cursor-pointer border-slate-500",
      )}
    >
      <span className="absolute inset-0 z-10" />
      <input
        tabIndex={-1}
        ref={InputRef}
        className={twMerge(
          classNames(
            "peer outline-none cursor-pointer text-primaryBlue-700 focus-none text-sm",
            "disabled:bg-transparent disabled:text-primaryBlue-700 font-medium",
            {"placeholder:text-transparent": label},
            {"px-2": layout === "bordered"},
          ),
          className,
        )}
        {...restProps}
        onChange={() => undefined}
      />
      <Label
        className="-translate-y-4 peer-placeholder-shown:translate-y-0"
        {...{layout}}
      >
        {label}
      </Label>
      <div
        className={classNames(
          "absolute w-2 -rotate-45 border-b-2 border-l-2 ",
          "aspect-square right-2 top-3 border-primary-400",
        )}
      />
    </div>
  );
};

const SelectFieldInput = ({children}: PropsWithChildren) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {showOptions, setShowOptions, InputRef, handleChange, ...restProps} =
    useContext(SelectFieldContext);

  return (
    <OutsideClickHandler
      onOutsideClick={() => setShowOptions((prev) => prev && !prev)}
      className="relative w-full"
    >
      <SelectFieldHelperInput
        onSelectClick={() => setShowOptions((prevState) => !prevState)}
        {...restProps}
      />
      <div
        className={classNames(
          "w-full absolute bg-white max-h-60 z-50 rounded overflow-y-scroll scroll-themed shadow-3xl",
          {hidden: !showOptions},
        )}
      >
        {children}
      </div>
    </OutsideClickHandler>
  );
};

export const SelectField = ({
  children,
  disableError,
  onValueChange,
  error,
  transitionedLabel = true,
  layout = "underlined",
  label,
  invalidationMessage,
  ...restProps
}: SelectFieldProps) => {
  const {setShowOptions, showOptions, handleChange, InputRef, input} =
    useSelect(restProps.name, "", error, onValueChange);

  return (
    <InputFieldContainer
      className="flex overflow-visible"
      {...{
        disableError,
        error:
          error ||
          (input?.error && (invalidationMessage || "Please select an option.")),
        layout,
        ...(!transitionedLabel && {label}),
      }}
    >
      <SelectFieldContext.Provider
        value={{
          InputRef,
          setShowOptions,
          showOptions,
          handleChange,
          layout,
          ...restProps,
          ...(transitionedLabel && {label}),
        }}
      >
        {children}
      </SelectFieldContext.Provider>
    </InputFieldContainer>
  );
};

SelectField.Input = SelectFieldInput;
SelectField.Option = Options;
